import React, { useMemo } from 'react';
import { Button, Col, Form, Input, notification, Row, Select, Space, Typography, DatePicker } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CloseOutlined, ContainerOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createUser } from '../../services/user';
import { AppSelect } from '../../constants/appSelect';
import { getAllCategories } from '../../services/category';
import dayjs from 'dayjs';

const itemName = 'Học viên';
const { Text, Title } = Typography;

const CreateUser = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { mutate: mutateCreate, isPending } = useMutation({
    mutationFn: createUser,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Thêm mới ${itemName} thành công!` });
        navigate(-1);
      } else {
        notification.error({ message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
      }
    },
    onError: (error) => notification.error({ message: error?.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...' }),
  });

  const handleSubmitForm = (data) => {
    const payload = {
      full_name: data?.full_name,
      phone: data?.phone,
      email: data?.email,
      password: data?.password, // server should hash it
      status: data?.status,
    };
    // enrollments mapping
    if (Array.isArray(data?.enrollments)) {
      const enrollments = data.enrollments
        .filter((e) => e && Number(e.category_id || 0) > 0)
        .map((e) => ({
          category_id: Number(e.category_id),
          start_date: e.start_date ? dayjs(e.start_date).toISOString() : undefined,
          expire_date: e.expire_date ? dayjs(e.expire_date).toISOString() : undefined,
        }));
      if (enrollments.length > 0) payload.enrollments = enrollments;
    }
    if (!payload?.password || String(payload.password).length < 6) {
      notification.warning({ message: 'Mật khẩu tối thiểu 6 ký tự' });
      return;
    }
    mutateCreate(payload);
  };

  // fetch categories for enrollment select (hierarchical labels)
  const { data: allCategoriesRes } = useQuery({
    queryKey: ['allCategoriesForUser'],
    queryFn: getAllCategories,
    staleTime: 1000 * 60 * 10,
  });
  const categoryOptions = useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    // Only include course categories (type === 2)
    const cats = all.filter((c) => Number(c?.type) === 2);
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id; const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) { guard.add(cur); parts.unshift(titleMap[cur] || ''); cur = parentMap[cur] || 0; }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const opts = cats.map((c) => ({ value: c.id, label: toPath(c.id) || (c.title || '') }));
    opts.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return opts;
  }, [allCategoriesRes]);

  return (
    <Form name="create-user" form={form} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Title level={3}>Thêm {itemName}</Title>
        </Col>

        <Col xs={12}>
          <Form.Item name="full_name" label={<Text>Họ và tên <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: `Vui lòng nhập họ và tên ${itemName}` }]}>
            <Input placeholder={`Nhập họ và tên ${itemName}...`} />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            name="phone"
            label={<Text>Số điện thoại <span className="text-red-700">*</span></Text>}
            rules={[
              { required: true, message: 'Vui lòng nhập số điện thoại' },
              { pattern: /^(0|\+84)(3|5|7|8|9)\d{8}$/, message: 'Số điện thoại không đúng định dạng (VN)' },
            ]}
          >
            <Input placeholder={`Nhập số điện thoại (VD: 09xxxxxxxx)...`} />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item
            name="email"
            label={<Text>Email</Text>}
            rules={[{ type: 'email', message: 'Email không đúng định dạng' }]}
          >
            <Input placeholder="Nhập email (không bắt buộc)..." />
          </Form.Item>
        </Col>

        <Col xs={12}>
          <Form.Item name="password" label={<Text>Mật khẩu <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}>
            <Input placeholder={`Nhập mật khẩu (tối thiểu 6 ký tự)...`} />
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item name="status" label={<Text>Trạng thái <span className="text-red-700">*</span></Text>} rules={[{ required: true, message: 'Vui lòng chọn trạng thái' }]} initialValue={1}>
            <Select showSearch placeholder="Chọn trạng thái" options={AppSelect.STATUS} allowClear />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <div className="mt-2 p-3 border rounded-md bg-white">
            <div className="flex justify-between items-center mb-2">
              <Text strong>Danh mục</Text>
            </div>
            <Form.List name="enrollments" initialValue={[]}> 
              {(fields, { add, remove }) => (
                <div className="space-y-3">
                  <Button type="dashed" onClick={() => add({ category_id: 0, start_date: null, expire_date: null })}>Thêm danh mục</Button>
                  {fields.map((field, idx) => (
                    <div key={field.key} className="p-3 border rounded-md">
                      <Row gutter={[12, 0]}>
                        <Col xs={24} md={10}>
                          <Form.Item
                            name={[field.name, 'category_id']}
                            label={<Text>Danh mục <span className="text-red-700">*</span></Text>}
                            rules={[
                              { required: true, message: 'Vui lòng chọn danh mục' },
                              {
                                validator: (_, value) =>
                                  Number(value || 0) > 0
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Vui lòng chọn danh mục')),
                              },
                            ]}
                          > 
                            <Select
                              showSearch
                              allowClear
                              placeholder="Chọn danh mục"
                              optionFilterProp="label"
                              options={[{ value: 0, label: 'Chọn danh mục' }, ...(categoryOptions || [])]}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                          <Form.Item
                            name={[field.name, 'start_date']}
                            label={<Text>Ngày bắt đầu <span className="text-red-700">*</span></Text>}
                            rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu' }]}
                          > 
                            <DatePicker className="w-full" format="DD/MM/YYYY" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                          <Form.Item
                            name={[field.name, 'expire_date']}
                            label={<Text>Ngày hết hạn <span className="text-red-700">*</span></Text>}
                            rules={[
                              { required: true, message: 'Vui lòng chọn ngày hết hạn' },
                              {
                                validator: (_, value) => {
                                  const start = form.getFieldValue(['enrollments', field.name, 'start_date']);
                                  if (!start || !value) return Promise.resolve();
                                  return value.isAfter(start, 'day')
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Ngày hết hạn phải sau ngày bắt đầu'));
                                },
                              },
                            ]}
                          >
                            <DatePicker className="w-full" format="DD/MM/YYYY" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={2} className="flex items-end">
                          <Button danger type="text" onClick={() => remove(field.name)}>Xóa</Button>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          </div>
        </Col>

        <Col xs={24}>
          <Row justify="end">
            <Col>
              <Space>
                <Button htmlType="button" icon={<CloseOutlined />} onClick={() => navigate(-1)}>Hủy bỏ</Button>
                <Button loading={isPending} htmlType="submit" type="primary" icon={<ContainerOutlined />}>Lưu lại</Button>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateUser;
